// src/beta/pages/Catalog.tsx
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wordmark } from '@/beta/components/Wordmark';
import { ChapterCard } from '@/beta/components/ChapterCard';
import { Eyebrow } from '@/beta/components/Eyebrow';
import { RedRule } from '@/beta/components/RedRule';
import { useCatalogQuery, usePrefetchShowcase } from '@/beta/hooks/useCatalogQuery';
import type { CatalogPayload } from '@/beta/types/catalog';

function pad2(n: number): string { return n < 10 ? `0${n}` : String(n); }

function StatRow({ totals }: { totals: CatalogPayload['totals'] }) {
  // photos/documents render '—' as a placeholder until the totals endpoint ships in Phase H.
  const photos = totals.photos > 0 ? pad2(totals.photos) : '—';
  const documents = totals.documents > 0 ? pad2(totals.documents) : '—';
  return (
    <div
      data-testid="vm-stat-row"
      style={{
        textAlign: 'center',
        fontFamily: 'var(--vm-font-heading)',
        fontSize: 12,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--vm-stone-light)',
      }}
    >
      {pad2(totals.vehicles)} vehicles
      <span style={{ margin: '0 var(--vm-s-2)', color: 'var(--vm-red)' }}>·</span>
      {photos} photos
      <span style={{ margin: '0 var(--vm-s-2)', color: 'var(--vm-red)' }}>·</span>
      {documents} documents
      <span style={{ margin: '0 var(--vm-s-2)', color: 'var(--vm-red)' }}>·</span>
      {pad2(totals.vaulted)} vaulted
    </div>
  );
}

function CatalogLoading() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--vm-s-3)',
      }}
    >
      <div style={{ width: 280, color: 'var(--vm-warm-white)' }}>
        <Wordmark width={280} />
      </div>
      <Eyebrow tone="stone">Loading edition</Eyebrow>
    </div>
  );
}

function CatalogError() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--vm-s-3)',
      }}
    >
      <div style={{ width: 280, color: 'var(--vm-warm-white)' }}>
        <Wordmark width={280} />
      </div>
      <Eyebrow tone="stone">Catalog unavailable</Eyebrow>
      <a
        href="/"
        style={{
          color: 'var(--vm-red)',
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 12,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          border: '1px solid var(--vm-red)',
          padding: '12px 18px',
          textDecoration: 'none',
        }}
      >
        Open legacy kiosk →
      </a>
    </div>
  );
}

/** /beta — Sleeping Beauties index. Spec § 6.1. */
export function Catalog() {
  const { data, isLoading, isError } = useCatalogQuery();
  const prefetchShowcase = usePrefetchShowcase();
  const navigate = useNavigate();

  useEffect(() => {
    if (!data) return;
    const heros = data.vehicles
      .map((v) => v.heroImageUrl)
      .filter((u): u is string => !!u);

    // requestIdleCallback isn't in older Safari; fall back to setTimeout.
    const ric: typeof window.requestIdleCallback =
      (window as any).requestIdleCallback ?? ((cb: IdleRequestCallback) => setTimeout(cb, 0));
    const cic: typeof window.cancelIdleCallback =
      (window as any).cancelIdleCallback ?? clearTimeout;

    const handles: number[] = [];
    heros.forEach((url, i) => {
      const handle = ric(
        () => {
          const img = new Image();
          img.src = url;
        },
        { timeout: 200 + i * 200 } as IdleRequestOptions,
      );
      handles.push(handle as unknown as number);
    });
    return () => handles.forEach((h) => cic(h as unknown as number));
  }, [data]);

  if (isLoading) return <CatalogLoading />;
  if (isError || !data) return <CatalogError />;

  const handleActivate = (vehicleId: string) => {
    navigate(`/beta/showcase/${vehicleId}`);
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* Top band — 168px tall, wordmark centered, charcoal bottom rule */}
      <header
        style={{
          height: 168,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid var(--vm-charcoal)',
          color: 'var(--vm-warm-white)',
        }}
      >
        <div style={{ width: 360 }}>
          <Wordmark width={360} />
        </div>
      </header>

      {/* Edition micro-label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--vm-s-2)',
          padding: 'var(--vm-s-3) 0 var(--vm-s-2)',
          color: 'var(--vm-stone)',
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 10,
          letterSpacing: '0.4em',
          textTransform: 'uppercase',
        }}
      >
        <RedRule width={40} />
        {data.editionLabel}
        <RedRule width={40} />
      </div>

      {/* Stat row */}
      <div style={{ padding: '0 0 var(--vm-s-4)' }}>
        <StatRow totals={data.totals} />
      </div>

      {/* 3×2 grid */}
      <main
        style={{
          flex: 1,
          padding: '0 56px 56px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridAutoRows: '1fr',
          gap: 22,
        }}
      >
        {data.vehicles.map((v, i) => (
          <ChapterCard
            key={v.id}
            index={i + 1}
            vehicle={v}
            onActivate={handleActivate}
            onPrefetch={prefetchShowcase}
          />
        ))}
      </main>

      {/* Bottom strip — 56px */}
      <footer
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 56px',
          borderTop: '1px solid var(--vm-charcoal)',
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 11,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
        }}
      >
        <span style={{ color: 'var(--vm-stone-light)' }}>Tap a vehicle to enter</span>
        <span style={{ color: 'var(--vm-red)' }}>Workshop Recall · Beta</span>
      </footer>
    </div>
  );
}
